import type { TiltMap } from "../types/api";

/**
 * TiltMapChart — the 2x2 Tilt Map. Plots one or more properties by their
 * Tilt Map position (X = price vs market, Y = seller flexibility) and colors
 * each dot by quadrant. Reused by the Saved board (all properties) and the
 * single-property report (one highlighted dot).
 */

export interface TiltMapPoint {
  id: string;
  label: string;
  tiltMap: TiltMap | null;
  highlight?: boolean;
  // Per-property color (Saved board uses a distinct spectrum so each dot is
  // identifiable, esp. on mobile); falls back to the quadrant color.
  color?: string;
  // When set, the dot and its legend label link to the property's report.
  onClick?: () => void;
}

const QUADRANT_COLOR: Record<string, string> = {
  Buy: "#1b7f4b",
  Negotiate: "#2563eb",
  Consider: "#b45309",
  Evaluate: "#6b7280",
  "Insufficient data": "#9ca3af",
};

function hasPos(tm: TiltMap | null): tm is TiltMap {
  return !!tm && tm.x_position != null && tm.y_position != null;
}

export function TiltMapChart({
  points,
  title,
}: {
  points: TiltMapPoint[];
  title?: string;
}) {
  const W = 380;
  const H = 392;
  const x0 = 70; // room for the rotated Flexibility labels on the left
  const y0 = 18;
  const x1 = W - 16;
  const y1 = H - 70; // room for the Price labels below
  // X is inverted so the BEST quadrant (Buy = below market + flexible seller)
  // lands top-right and the worst (Evaluate = above market + firm) bottom-left —
  // the convention people read from a 2x2. So a high x_position (above market)
  // renders on the LEFT, a low x_position (below market) on the RIGHT.
  const sx = (x: number) => x1 - (x / 100) * (x1 - x0);
  const sy = (y: number) => y1 - (y / 100) * (y1 - y0); // invert: high flex = top
  const midX = sx(50);
  const midY = sy(50);
  const leftCx = (x0 + midX) / 2;
  const rightCx = (midX + x1) / 2;

  // Gartner-style quadrant label: centered in its quadrant, in a shaded pill.
  const pill = (cx: number, baselineY: number, label: string) => {
    const w = label.length * 8 + 20;
    return (
      <g>
        <rect
          x={cx - w / 2}
          y={baselineY - 14}
          width={w}
          height={19}
          rx={9.5}
          className="tm-q-pill"
        />
        <text x={cx} y={baselineY} textAnchor="middle" className="tm-q">
          {label}
        </text>
      </g>
    );
  };

  const plotted = points.filter((p) => hasPos(p.tiltMap));
  const unplaced = points.filter((p) => !hasPos(p.tiltMap));

  return (
    <div className="tilt-map-chart">
      {title && <h2 className="tilt-map-title">{title}</h2>}
      <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label="Tilt Map of properties">
        {/* quadrant background tints — best (Buy) top-right per convention */}
        <rect x={x0} y={y0} width={midX - x0} height={midY - y0} fill="#2563eb14" />
        <rect x={midX} y={y0} width={x1 - midX} height={midY - y0} fill="#1b7f4b14" />
        <rect x={x0} y={midY} width={midX - x0} height={y1 - midY} fill="#6b728014" />
        <rect x={midX} y={midY} width={x1 - midX} height={y1 - midY} fill="#b4530914" />

        {/* quadrant labels — centered in shaded pills (Gartner-style) */}
        {pill(leftCx, y0 + 18, "Negotiate")}
        {pill(rightCx, y0 + 18, "Buy")}
        {pill(leftCx, y1 - 10, "Evaluate")}
        {pill(rightCx, y1 - 10, "Consider")}

        {/* axes */}
        <line x1={midX} y1={y0} x2={midX} y2={y1} className="tm-axis" />
        <line x1={x0} y1={midY} x2={x1} y2={midY} className="tm-axis" />
        <rect x={x0} y={y0} width={x1 - x0} height={y1 - y0} className="tm-frame" />

        {/* X axis — Price: poles at the ends, name centered below */}
        <text x={x0} y={y1 + 20} textAnchor="start" className="tm-pole">
          Above market
        </text>
        <text x={x1} y={y1 + 20} textAnchor="end" className="tm-pole">
          Below market
        </text>
        <text x={(x0 + x1) / 2} y={y1 + 42} textAnchor="middle" className="tm-axis-name">
          Asking Price
        </text>

        {/* Y axis — Flexibility: poles at the ends, name centered (rotated) */}
        <text
          x={32}
          y={y0 + 34}
          textAnchor="middle"
          className="tm-pole"
          transform={`rotate(-90 32 ${y0 + 34})`}
        >
          Flexible
        </text>
        <text
          x={32}
          y={y1 - 30}
          textAnchor="middle"
          className="tm-pole"
          transform={`rotate(-90 32 ${y1 - 30})`}
        >
          Firm
        </text>
        <text
          x={14}
          y={(y0 + y1) / 2}
          textAnchor="middle"
          className="tm-axis-name"
          transform={`rotate(-90 14 ${(y0 + y1) / 2})`}
        >
          Seller Flexibility
        </text>

        {/* dots */}
        {plotted.map((p) => {
          const tm = p.tiltMap as TiltMap;
          const cx = sx(tm.x_position as number);
          const cy = sy(tm.y_position as number);
          const color = p.color ?? QUADRANT_COLOR[tm.quadrant] ?? "#555";
          const cls = [
            "tm-dot",
            p.highlight ? "tm-dot-hi" : "",
            p.onClick ? "tm-dot-link" : "",
          ]
            .filter(Boolean)
            .join(" ");
          return (
            <circle
              key={p.id}
              cx={cx}
              cy={cy}
              r={p.highlight ? 8 : 6}
              fill={color}
              stroke="#fff"
              strokeWidth={p.highlight ? 2.5 : 1.5}
              className={cls}
              onClick={p.onClick}
            >
              {/* hover (desktop) reveals the address; mobile taps the dot or
                  uses the legend links below */}
              <title>{`${p.label} — ${tm.quadrant}`}</title>
            </circle>
          );
        })}
      </svg>

      {plotted.length > 0 && (
        <ul className="tm-legend">
          {plotted.map((p) => {
            const tm = p.tiltMap as TiltMap;
            return (
              <li key={p.id} className={p.highlight ? "tm-legend-hi" : undefined}>
                <span
                  className="tm-swatch"
                  style={{ background: p.color ?? QUADRANT_COLOR[tm.quadrant] ?? "#555" }}
                  aria-hidden
                />
                {p.onClick ? (
                  <button type="button" className="tm-legend-link" onClick={p.onClick}>
                    {p.label}
                  </button>
                ) : (
                  <span className="tm-legend-label">{p.label}</span>
                )}
                <span className="tm-legend-q">{tm.quadrant}</span>
              </li>
            );
          })}
        </ul>
      )}

      {unplaced.length > 0 && (
        <p className="hint">
          {unplaced.length} saved{" "}
          {unplaced.length === 1 ? "property hasn't" : "properties haven't"} been
          scored under the Tilt Map yet — open one to place it on the map.
        </p>
      )}
    </div>
  );
}
